// MobileContextMenuContext — controlled state for long-press → context menu
// on touch devices.
//
// We can't reliably trigger Radix's <ContextMenu> from a synthetic
// `dispatchEvent('contextmenu', ...)` on mobile (Radix uses pointer-
// event tracking that ignores synthesized events). Instead the
// touch-handler in canvas-touch.ts calls into this context, which
// renders a small positioned menu at the touched point with kind-
// specific entries.
//
// Desktop right-click still uses the existing <ContextMenu> wrappers —
// this is touch-only.

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type MobileCtxMenuKind = 'block' | 'group-card' | 'canvas';

export interface MobileCtxMenuState {
  kind: MobileCtxMenuKind;
  /** Target id — block id, group id, or undefined for canvas. */
  id?: string;
  /** Screen coordinates from the long-press origin. */
  x: number;
  y: number;
}

export interface MobileContextMenuValue {
  state: MobileCtxMenuState | null;
  open: (s: MobileCtxMenuState) => void;
  close: () => void;
}

const Ctx = createContext<MobileContextMenuValue | null>(null);

export function MobileContextMenuProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<MobileCtxMenuState | null>(null);
  const open = useCallback((s: MobileCtxMenuState) => setState(s), []);
  const close = useCallback(() => setState(null), []);
  const value = useMemo(() => ({ state, open, close }), [state, open, close]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useMobileContextMenu(): MobileContextMenuValue {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error('useMobileContextMenu must be used within <MobileContextMenuProvider>');
  }
  return v;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useMobileContextMenuOptional(): MobileContextMenuValue | null {
  return useContext(Ctx);
}
